import type { RouteSettings } from '../../../shared/types';
import { shell } from '../app';
import { ApiError, NetworkError } from '../data/api';
import { keepRouteSettings, loadRouteSettings } from '../data/corridor';
import { getSettings, putSettings } from '../data/daily';
import { settingsProblem } from '../logic/daily';
import type { SettingsDraft } from '../logic/daily';
import { btn, chips, empty, field, input, segmented } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import { navigate } from '../ui/router';
import type { View } from '../ui/router';

/** How Plan tomorrow plans a day (D83): one set of settings in the account, synced to both devices. */

const WEEK = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '0', label: 'Sun' },
] as const;

const friendly = (err: unknown): string =>
  err instanceof NetworkError
    ? 'Saving the settings needs a connection.'
    : err instanceof ApiError
      ? err.message
      : 'Something went wrong. Try again.';

const whole = (v: string) => (/^\d+$/.test(v.trim()) ? Number(v) : NaN);

export const routeSettingsView: View = async ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Route settings');
  const host = h('div', { class: 'page page--settings' });
  root.appendChild(host);

  let current: RouteSettings;
  try {
    current = await getSettings().catch(async (err: unknown) => {
      const kept = await loadRouteSettings();
      if (!kept) throw err;
      return kept;
    });
  } catch (err) {
    replaceChildren(host, empty(friendly(err)));
    return;
  }

  const form = {
    home: current.home_label,
    start: current.start_time,
    hours: String(current.day_minutes / 60),
    stop: String(current.stop_minutes),
    meeting: String(current.meeting_minutes),
    target: String(current.target_stops),
  };
  const workdays = new Set(current.workdays.map(String));
  let warmPriority = current.warm_priority === true;
  let avoidRush = current.avoid_rush !== false;
  // each input by the API field it feeds, so a refused value can be focused with its toast
  const inputs: Partial<Record<keyof SettingsDraft, HTMLInputElement>> = {};
  const text = (key: keyof typeof form, sends: keyof SettingsDraft, type = 'text', inputmode?: string) =>
    (inputs[sends] = input({ value: form[key], type, inputmode, onInput: (v) => (form[key] = v) }));

  const save = async () => {
    const hours = Number(form.hours);
    const patch = {
      home_label: form.home.trim(),
      start_time: form.start,
      day_minutes: Math.round(hours * 60),
      stop_minutes: whole(form.stop),
      meeting_minutes: whole(form.meeting),
      target_stops: whole(form.target),
      workdays: [...workdays].map(Number).sort((a, b) => a - b),
      avoid_rush: avoidRush,
      warm_priority: warmPriority,
    };
    if (
      !form.hours.trim() ||
      Number.isNaN(hours) ||
      [patch.stop_minutes, patch.meeting_minutes, patch.target_stops].some(Number.isNaN)
    )
      return shell.toast('Use whole numbers for the minutes and the stops.', { kind: 'error' });
    const problem = settingsProblem(patch);
    if (problem) {
      inputs[problem.field]?.focus();
      return shell.toast(problem.text, { kind: 'error' });
    }
    try {
      const changed = Object.fromEntries(Object.entries(patch).filter(([key, value]) =>
        JSON.stringify(value) !== JSON.stringify(current[key as keyof RouteSettings] ?? (key === 'warm_priority' ? false : undefined))));
      const saved = Object.keys(changed).length ? await putSettings(changed) : current;
      await keepRouteSettings(saved);
      shell.toast('Settings saved', { kind: 'success' });
      navigate('#/route/plan');
    } catch (err) {
      shell.toast(friendly(err), { kind: 'error' });
    }
  };

  replaceChildren(
    host,
    field('Prioritize due follow-ups', segmented({
      options: [{value:'off', label:'Off'}, {value:'on', label:'On'}],
      value: warmPriority ? 'on' : 'off', onChange: v => { warmPriority = v === 'on'; }, size:'md',
    }), {hint:'Reserve follow-up visits first. This can mean more driving and fewer new doors.'}),
    field('Home address', text('home', 'home_label'), { hint: 'Where each day starts' }),
    field('Start time', text('start', 'start_time', 'time')),
    field('Day length (hours)', text('hours', 'day_minutes', 'number', 'decimal'), {
      hint: 'The drive home is not counted',
    }),
    field('Minutes per stop', text('stop', 'stop_minutes', 'number', 'numeric')),
    field('Minutes per meeting', text('meeting', 'meeting_minutes', 'number', 'numeric')),
    field('Stops a day', text('target', 'target_stops', 'number', 'numeric')),
    field(
      'Workdays',
      chips({
        options: WEEK,
        selected: workdays,
        onToggle: (day, on) => (on ? workdays.add(day) : workdays.delete(day)),
      }),
    ),
    field(
      'Busy hours',
      segmented({
        options: [
          { value: 'skip', label: 'Skip them' },
          { value: 'plan', label: 'Plan them too' },
        ],
        value: avoidRush ? 'skip' : 'plan',
        onChange: (v) => (avoidRush = v === 'skip'),
        size: 'md',
      }),
      { hint: 'Restaurants at lunch and dinner, coffee shops before 9, bars from 5:30: owners are busy' },
    ),
    h('div', { class: 'planbar' }, btn('Save', { variant: 'primary', onClick: () => void save() })),
  );
};
